/**
 * BottomTabBar — Feb 2026 (Step 3 · Slice 2)
 *
 * Persistent bottom tab bar for dispatcher / super-admin users on mobile.
 * Renders 5 domain buttons matching the info-architecture regroup:
 *
 *   OPS       cyan     /dispatch                          — order queue home
 *   GROWTH    orange   /dispatch/admin/launch-stats       — growth dashboard
 *   REVIEWS   yellow   /dispatch/admin/reviews            — moderation queue
 *   PEOPLE    green    /dispatch/admin/users              — customer directory
 *   SYSTEM    purple   /dispatch/settings                 — settings + config
 *
 * Hidden on non-dispatch routes (customer/driver views) and for logged-out
 * users. On desktop (>=lg breakpoint) collapses into a persistent left rail
 * variant — same items, different layout — TODO in the next step of Slice 2.
 *
 * Body-padding safeguard: each dispatch page's own layout should include a
 * `pb-16` (or safe-area-aware equivalent) so the tab bar doesn't cover
 * content. Rather than mutate every page, we set the padding globally via
 * an effect that toggles a body class only while this bar is mounted.
 */
import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Radio, TrendingUp, Star, Users, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { prefetchDomain } from '../lib/prefetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'ops',     label: 'OPS',      to: '/dispatch',                     icon: Radio,       accent: '#00F0FF' },
  { key: 'growth',  label: 'GROWTH',   to: '/dispatch/admin/launch-stats',  icon: TrendingUp,  accent: '#FF9F0A' },
  { key: 'reviews', label: 'REVIEWS',  to: '/dispatch/admin/reviews',       icon: Star,        accent: '#FFD60A' },
  { key: 'people',  label: 'PEOPLE',   to: '/dispatch/admin/users',         icon: Users,       accent: '#39FF14' },
  { key: 'system',  label: 'SYSTEM',   to: '/dispatch/settings',            icon: Settings,    accent: '#B87BFF' },
];

// Given the current pathname, which tab should be "active"?
// Anything under /dispatch/admin/reviews → REVIEWS, etc.
// Order matters: check more-specific prefixes before the generic /dispatch.
function activeTabKey(pathname) {
  if (pathname.startsWith('/dispatch/admin/reviews')) return 'reviews';
  if (pathname.startsWith('/dispatch/admin/launch-stats')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/leaderboards')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/promo-agent')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/boost')) return 'growth';
  if (pathname.startsWith('/dispatch/admin/users')) return 'people';
  if (pathname.startsWith('/dispatch/admin/points')) return 'people';
  if (pathname.startsWith('/dispatch/settings')) return 'system';
  if (pathname.startsWith('/dispatch/admin/edit-profile')) return 'system';
  if (pathname.startsWith('/dispatch/admin/login-stats')) return 'system';
  if (pathname.startsWith('/dispatch/admin/push-health')) return 'system';
  if (pathname.startsWith('/dispatch/admin/email-health')) return 'system';
  // Everything else under /dispatch (order queues, broadcast, regions, data-quality,
  // failed-orders, /dispatch itself) belongs to OPS
  if (pathname.startsWith('/dispatch')) return 'ops';
  return null;
}

export function BottomTabBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const isDispatchScope = pathname.startsWith('/dispatch');
  const isAuthorized = user && (user.role === 'dispatcher' || user.role === 'super_admin');
  const shouldShow = isAuthorized && isDispatchScope;

  // Feb 2026 (Step 3 · Slice 2 · badges) — Poll unread counts every 30s.
  // OPS badge = customer-chat unread total. REVIEWS badge = pending moderation.
  // Skips fetches when the bar isn't shown, and cancels in-flight requests
  // on unmount so we don't leak on route change.
  const [badges, setBadges] = useState({ ops: 0, reviews: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!shouldShow) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const [chatRes, revRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/chat`, { withCredentials: true })
            .catch(() => ({ data: { unread_total: 0 } })),
          axios.get(`${API_URL}/api/admin/reviews/pending-count`, { withCredentials: true })
            .catch(() => ({ data: { count: 0 } })),
        ]);
        if (cancelled) return;
        setBadges({
          ops: Number(chatRes.data?.unread_total || 0),
          reviews: Number(revRes.data?.count || 0),
        });
      } catch {
        // silent — badges default to 0 on any error
      }
    };
    load();
    timerRef.current = setInterval(load, 30000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [shouldShow]);

  // Add global padding-bottom on the body only while the tab bar is mounted
  // AND on mobile viewports, so pages don't have their footer content hidden.
  useEffect(() => {
    if (!shouldShow) return;
    document.body.classList.add('has-bottom-tab-bar');
    return () => document.body.classList.remove('has-bottom-tab-bar');
  }, [shouldShow]);

  if (!shouldShow) return null;

  const active = activeTabKey(pathname);

  // Feb 2026 — Fluidity Instant Triggers.
  // `onPointerDown` + `onTouchStart` fire the millisecond a finger touches
  // the screen (before tap release), racing the fetch against the tap
  // animation instead of after it. `onPointerEnter` keeps the desktop
  // hover-warmup, `onFocus` covers keyboard nav.
  // prefetchQuery is idempotent — if the cache is fresh (within staleTime)
  // it returns immediately without a network hit.
  const warm = (tabKey) => prefetchDomain(queryClient, tabKey);

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0a0510]/95 backdrop-blur border-t border-[#2A0F3D]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="bottom-tab-bar"
      aria-label="Primary navigation"
    >
      <ul className="flex justify-around items-stretch">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = active === tab.key;
          const badge = badges[tab.key] || 0;
          return (
            <li key={tab.key} className="flex-1">
              <Link
                to={tab.to}
                data-testid={`bottom-tab-${tab.key}`}
                onPointerDown={() => warm(tab.key)}
                onTouchStart={() => warm(tab.key)}
                onPointerEnter={() => warm(tab.key)}
                onFocus={() => warm(tab.key)}
                className="relative flex flex-col items-center gap-0.5 py-2 px-1 font-mono text-[9px] uppercase tracking-wider transition-colors"
                style={{
                  color: isActive ? tab.accent : '#4B6B52',
                  borderTop: isActive ? `2px solid ${tab.accent}` : '2px solid transparent',
                }}
              >
                <span className="relative inline-flex">
                  <Icon className="w-4 h-4" strokeWidth={isActive ? 2.25 : 1.75} />
                  {badge > 0 && (
                    <span
                      data-testid={`bottom-tab-${tab.key}-badge`}
                      className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center leading-none"
                      style={{
                        backgroundColor: '#FF3B30',
                        color: '#0a0510',
                      }}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </span>
                <span>{tab.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default BottomTabBar;
